import { useId, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../../lib/apiClient';
import { createPersonalMailbox } from '../../../lib/mailAdminApi';
import type { MailAdminCandidateDto, MailAdminMailboxDto } from '../../../lib/mailAdminTypes';
import {
  MAIL_DOMAINS,
  filterCandidates,
  freeMailDomains,
  previewPersonalLocalPart,
  splitName,
  validateLocalPart,
} from '../../../lib/mailAdminValidation';
import { AdminDialog, FieldStack } from './AdminDialog';

export function CreatePersonalMailboxDialog({
  candidates,
  onClose,
  onCreated,
}: {
  candidates: readonly MailAdminCandidateDto[];
  onClose: () => void;
  onCreated: (mailbox: MailAdminMailboxDto) => void;
}) {
  const formId = useId();
  const [query, setQuery] = useState('');
  const [uid, setUid] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [domain, setDomain] = useState<string>(MAIL_DOMAINS[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A user can own several personal mailboxes, one per domain: they stay in the list until every
  // domain has one.
  const options = useMemo(
    () => filterCandidates(candidates, { query, onlyWithFreeDomain: true }),
    [candidates, query],
  );
  const eligibleCount = useMemo(
    () => filterCandidates(candidates, { onlyWithFreeDomain: true }).length,
    [candidates],
  );
  const picked = candidates.find((c) => c.uid === uid);
  const domains: readonly string[] = picked ? freeMailDomains(picked) : MAIL_DOMAINS;

  const local = previewPersonalLocalPart(firstName, lastName);
  const localError = local ? validateLocalPart(local) : null;
  const valid = !!uid && !!firstName.trim() && !!local && !localError;

  const pick = (nextUid: string) => {
    setUid(nextUid);
    setError(null);
    const candidate = candidates.find((c) => c.uid === nextUid);
    if (candidate) {
      const split = splitName(candidate.name);
      setFirstName(split.firstName);
      setLastName(split.lastName);
      // Default to a domain this person does not have a personal mailbox on yet.
      const free = freeMailDomains(candidate);
      if (free.length > 0 && !free.includes(domain)) setDomain(free[0]);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createPersonalMailbox({
        uid,
        firstName: firstName.trim(),
        ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
        domain,
      });
      onCreated(created);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the mailbox.');
      setBusy(false);
    }
  };

  return (
    <AdminDialog
      title="Create personal mailbox"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form={formId} className="btn btn-primary" disabled={!valid || busy}>
            {busy ? 'Creating…' : 'Create mailbox'}
          </button>
        </>
      }
    >
      <form id={formId} onSubmit={(e) => void submit(e)} noValidate>
        <FieldStack>
          <label htmlFor={`${formId}-search`}>Find a person</label>
          <input
            id={`${formId}-search`}
            type="search"
            value={query}
            placeholder="Search by name or email"
            onChange={(e) => setQuery(e.target.value)}
          />

          <label htmlFor={`${formId}-user`}>Portal user</label>
          <select id={`${formId}-user`} value={uid} onChange={(e) => pick(e.target.value)}>
            <option value="">Select a user…</option>
            {options.map((c) => (
              <option key={c.uid} value={c.uid}>
                {c.name} ({c.email})
                {(c.personalMailboxDomains ?? []).length > 0
                  ? ` — already on ${(c.personalMailboxDomains ?? []).join(', ')}`
                  : ''}
              </option>
            ))}
          </select>
          <div className="form-hint">
            {eligibleCount === 0
              ? 'Every eligible user already has a personal mailbox on every domain.'
              : options.length === 0
                ? 'No eligible user matches that search.'
                : 'A user can have one personal mailbox per domain; people stay listed until each domain has one.'}
          </div>

          <label htmlFor={`${formId}-first`}>First name</label>
          <input
            id={`${formId}-first`}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="off"
          />

          <label htmlFor={`${formId}-last`}>Last name (optional)</label>
          <input
            id={`${formId}-last`}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="off"
          />

          <label htmlFor={`${formId}-domain`}>Domain</label>
          <select id={`${formId}-domain`} value={domain} onChange={(e) => setDomain(e.target.value)}>
            {domains.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          <div className="form-hint" aria-live="polite" data-testid="address-preview">
            {local && !localError
              ? `Address preview: ${local}@${domain} (the server adds a number if it is taken)`
              : localError
                ? localError
                : 'Address preview appears once a name is entered.'}
          </div>

          {error && (
            <div className="error-banner" role="alert" style={{ marginBottom: 0 }}>
              {error}
            </div>
          )}
        </FieldStack>
      </form>
    </AdminDialog>
  );
}
